import { useEffect, useMemo, useState } from "react";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import CalculatorSection from "@/components/site/CalculatorSection";
import ResultsSection from "@/components/site/ResultsSection";
import CorpusChart from "@/components/site/CorpusChart";
import DistributionChart from "@/components/site/DistributionChart";
import IncomeRoadmap from "@/components/site/IncomeRoadmap";
import AboutSection from "@/components/site/AboutSection";
import ContactSection from "@/components/site/ContactSection";
import Footer from "@/components/site/Footer";
import EntryGateDialog from "@/components/site/EntryGateDialog";
import DownloadPdfButton from "@/components/site/DownloadPdfButton";
import { computePlan } from "@/lib/calculator";

const RETURNING_VISITOR_KEY = "sahiDishaVisitor";

export default function CalculatorPage() {
  const [inputs, setInputs] = useState({
    monthlyExpenses: 75000,
    currentAge: 30,
    freedomAge: 50,
    annualReturn: 0.12,
    annualInflation: 0.06,
    stepupPct: 0.1,
  });

  // Entry-gate state — user must submit name+mobile before seeing real calc results,
  // UNLESS this device has already captured a lead (stored in localStorage).
  const [entered, setEntered] = useState(false);
  const [leadId, setLeadId] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RETURNING_VISITOR_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved?.leadId) {
        setLeadId(saved.leadId);
        setEntered(true);
      }
    } catch (err) {
      // malformed localStorage; log and proceed — gate will be shown
      console.error("Failed to read returning-visitor state:", err);
    }
  }, []);

  const plan = useMemo(() => computePlan(inputs), [inputs]);

  const scrollToCalc = () => {
    document
      .getElementById("calculator")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleEntry = ({ leadId: id, firstName, mobile }) => {
    setLeadId(id || null);
    setEntered(true);
    try {
      localStorage.setItem(
        RETURNING_VISITOR_KEY,
        JSON.stringify({ leadId: id || null, firstName: firstName || "", mobile: mobile || "" }),
      );
    } catch (err) {
      // localStorage disabled/quota exceeded — session-only entry still works
      console.error("Failed to persist visitor state:", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <Header onPrimaryClick={scrollToCalc} leadId={leadId} inputs={inputs} entered={entered} />
      <Hero onPrimaryClick={scrollToCalc} />

      <main id="calculator" className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-12 lg:py-16">
          <div className="lg:col-span-7">
            <CalculatorSection inputs={inputs} setInputs={setInputs} />
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start">
            <ResultsSection
              plan={plan}
              inputs={inputs}
              leadId={leadId}
              entered={entered}
            />
          </div>
        </div>

        <CorpusChart plan={plan} />
        <DistributionChart plan={plan} />
        <IncomeRoadmap plan={plan} />

        <div
          className="mt-8 rounded-2xl border border-[#F97316]/20 bg-gradient-to-r from-[#F97316]/10 to-transparent p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="roadmap-download-cta"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#F97316]">
              Take this with you
            </div>
            <h3 className="mt-1 font-heading text-xl sm:text-2xl font-semibold tracking-tight">
              Get your personalised blueprint — PDF
            </h3>
            <p className="mt-1 text-sm text-slate-400">
              Your roadmap, corpus target and SIP plan — delivered as a branded document.
            </p>
            <div
              className="mt-4 rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-[11px] text-slate-400 leading-relaxed max-w-xl"
              data-testid="download-disclaimer-bottom"
            >
              <span className="text-[#FDBA74] font-medium">Disclaimer:</span> Mutual fund investments
              are subject to market risks. Please read all scheme related documents carefully.
            </div>
          </div>
          <DownloadPdfButton
            inputs={inputs}
            leadId={leadId}
            entered={entered}
            variant="secondary"
            testId="download-pdf-btn-bottom"
            label="Download the Blueprint (PDF)"
            shortLabel="Download Blueprint"
          />
        </div>
      </main>

      <AboutSection />
      <ContactSection />
      <Footer />

      <EntryGateDialog open={!entered} onSuccess={handleEntry} />
    </div>
  );
}
